import {
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
} from "@headlessui/react";
import clsx from "clsx";
import { ChevronDown, ChevronUp } from "lucide-react";
import React from "react";
import { AppText } from "@/components/Text/AppText";
import { JsonEditorComponent } from "@/pages/Admin/JsonEditorComponent/JsonEditorComponent";

interface ExpandableDebugItemProps {
    label: string;
    value: string;
}

const keyToDetailsMap = {
    extractedText: {
        label: "Extracted Text",
        description: "The value we get back from our PDF/DOCX parser",
        step: 1,
        isAi: false,
    },
    extractedTextFromFile: {
        label: "Extracted Text From File",
        description: "The value we get back from our PDF/DOCX parser",
        step: 1,
        isAi: false,
    },
    sanitizedResume: {
        label: "Sanitized Resume",
        description: "Currently disabled - this value is not used",
        step: 1,
        isAi: false,
    },
    profileData: {
        label: "Profile Data",
        description:
            "This is part AI and part user account details. Used to help us put together the profile section of the resume.",
        step: 1,
        isAi: false,
    },
    sensitiveData: {
        label: "Sensitive Data",
        description:
            "Not currently used - this was a way for us to extract potentially sensitive data from raw resume input",
        step: 1,
        isAi: false,
    },
    cleanedRawResume: {
        label: "Cleaned Raw Resume",
        description:
            "We pre-process the raw resume input to remove any invalid formatting or characters for safer processing",
        step: 2,
        isAi: true,
    },
    jobInsights: {
        label: "Job Description Insights",
        description:
            "If a JD is present, we analyse it into a more structured format",
        step: 3,
        isAi: true,
    },
    formattedResumeSections: {
        label: "Formatted Resume Sections",
        description:
            "This is where we break down the resume sections into our structure with our known props for each section.",
        step: 4,
        isAi: true,
    },
    cleanedResumeSections: {
        label: "Resume Splitting",
        description:
            "From the raw resume input, we split the data into known sections",
        step: 3,
        isAi: true,
    },
    isJobDescription: {
        label: "Is Job Description",
        description:
            "Is the submitted job description actually a job description?",
        step: 5,
        isAi: true,
    },
    tailoredItems: {
        label: "Tailored Items",
        description:
            "We take the JD and the section fields that are details oriented and tailor them to the JD, with reasoning",
        step: 6,
        isAi: true,
    },
    tailoredResumeSections: {
        label: "Tailored Resume Sections",
        description:
            "This is the result of putting the tailored values back into the resume sections",
        step: 7,
        isAi: false,
    },
    createdResume: {
        label: "Created Resume",
        description:
            "The resume object thats been created from the importer process.",
        step: 8,
        isAi: false,
    },
};

export const ExpandableDebugItem: React.FC<ExpandableDebugItemProps> = ({
    label,
    value = "No value",
}) => {
    let displayValue = value;
    let isJson = false;
    try {
        if (value?.startsWith("{") && value?.endsWith("}")) {
            displayValue = JSON.stringify(JSON.parse(value), null, 2);
            isJson = true;
        }
    } catch (e) {
        // fallback to raw value
    }
    const preview =
        displayValue.length > 100
            ? displayValue.slice(0, 100) + "..."
            : displayValue;
    const isExpandable = displayValue.length > 100;

    const isAi = keyToDetailsMap[label]?.isAi ?? false;
    const step = keyToDetailsMap[label]?.step ?? -1;
    const labelValue = keyToDetailsMap[label]?.label ?? label;
    const description = keyToDetailsMap[label]?.description ?? "No description";

    return (
        <Disclosure>
            {({ open }) => (
                <div className="mb-4 rounded-lg border bg-neutral-50">
                    <DisclosureButton className="flex w-full items-center justify-between px-4 py-2 text-left">
                        <div className="flex w-full flex-col gap-2">
                            <AppText
                                variant="footnote"
                                className="truncate !font-medium"
                            >
                                [ Step {step} ] - {labelValue} - {description}
                                {isAi && " (AI)"}
                            </AppText>
                            <AppText
                                variant="footnote"
                                className={clsx(
                                    "w-[900px] text-neutral-700",
                                    !open && "overflow-hidden truncate",
                                )}
                            >
                                {open || !isExpandable ? preview : displayValue}
                            </AppText>
                        </div>
                        {isExpandable && (
                            <span className="ml-2 flex items-center">
                                <AppText
                                    variant="labelsbuttons"
                                    className="mr-1 whitespace-nowrap"
                                >
                                    {open ? "Show less" : "Show more"}
                                </AppText>
                                {open ? (
                                    <ChevronUp className="h-5 w-5" />
                                ) : (
                                    <ChevronDown className="h-5 w-5" />
                                )}
                            </span>
                        )}
                    </DisclosureButton>
                    {isExpandable && (
                        <DisclosurePanel static>
                            {open && !isJson && (
                                <pre className="whitespace-pre-wrap break-all bg-neutral-50 px-4 pb-2 pt-1 text-xs text-neutral-800">
                                    {displayValue}
                                </pre>
                            )}
                            {open && isJson && (
                                <JsonEditorComponent
                                    jsonData={JSON.parse(value)}
                                    onUpdate={() => {}}
                                    id={label}
                                    viewOnly
                                    className="h-full w-full !max-w-full"
                                    rootFontSize="12px"
                                    containerClassName="h-full w-full"
                                    stringTruncate={5000}
                                />
                            )}
                        </DisclosurePanel>
                    )}
                </div>
            )}
        </Disclosure>
    );
};
